import React, { useCallback, useEffect, useState } from 'react';
import { getTimeseries, getTechnology, getHeatmap, getEngagement, errorMessage } from '../api/securityApi';
import SecurityNav from '../components/Security/SecurityNav';
import SiteSelector, { useActiveSite } from '../components/Security/SiteSelector';
import { LineChart, BarList, Donut, Heatmap } from '../components/Security/Charts';

const nameVal = (arr) => (arr || []).map((x) => ({ label: x.name, value: x.count }));

export default function AnalyticsPage() {
  const { sites, siteId, setSiteId } = useActiveSite();
  const [days, setDays] = useState(14);
  const [d, setD] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [ts, tech, heat, eng] = await Promise.all([
        getTimeseries(siteId, days), getTechnology(siteId, days), getHeatmap(siteId, days), getEngagement(siteId, days)]);
      setD({ ts, tech, heat, eng });
    } catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  }, [siteId, days]);
  useEffect(() => { load(); }, [load]);

  const pts = (d.ts?.points || []).map((p) => ({ ...p, label: p.date.slice(5) }));
  const sum = (k) => pts.reduce((a, p) => a + p[k], 0);
  const eng = d.eng;
  const fmtSec = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`);

  return (
    <div className="shell">
      <header className="page-header"><div><h1>Traffic analytics</h1><p className="muted">Sessions, engagement and visitor technology.</p></div></header>
      <SecurityNav />
      <div className="sec-toolbar">
        <SiteSelector sites={sites} value={siteId} onChange={setSiteId} />
        <select className="sec-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[7, 14, 30, 90].map((x) => <option key={x} value={x}>Last {x} days</option>)}</select>
        <button className="ghost" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>
      {error && <div className="toast error">{error}</div>}

      {d.ts && (
        <div className="sec-kpis">
          <div className="sec-kpi"><small>Sessions</small><b>{sum('sessions')}</b></div>
          <div className="sec-kpi"><small>Pageviews</small><b>{sum('pageviews')}</b></div>
          <div className="sec-kpi"><small>Clicks</small><b>{sum('clicks')}</b></div>
          {eng && <>
            <div className="sec-kpi"><small>Avg. session</small><b>{fmtSec(eng.avgDurationSeconds)}</b><small>median {fmtSec(eng.medianDurationSeconds)}</small></div>
            <div className="sec-kpi"><small>Pages / session</small><b>{eng.pagesPerSession}</b></div>
            <div className="sec-kpi"><small>Bounce rate</small><b>{eng.bounceRate}%</b></div>
          </>}
        </div>
      )}

      <div className="sec-grid">
        <div className="sec-card" style={{ gridColumn: '1 / -1' }}><h3>Traffic over time</h3>
          <LineChart data={pts} series={[
            { key: 'sessions', label: 'Sessions', color: '#2d66e2' },
            { key: 'pageviews', label: 'Pageviews', color: '#10b981' },
            { key: 'clicks', label: 'Clicks', color: '#f59e0b' }]} />
          <p className="muted" style={{ fontSize: 12 }}>Blue: sessions · Green: pageviews · Amber: clicks</p></div>
        {d.tech && <>
          <div className="sec-card"><h3>Browsers</h3><Donut items={nameVal(d.tech.browsers)} /></div>
          <div className="sec-card"><h3>Operating systems</h3><BarList items={nameVal(d.tech.operatingSystems)} /></div>
          <div className="sec-card"><h3>Devices</h3><Donut items={nameVal(d.tech.devices)} /></div>
        </>}
        {eng && <div className="sec-card"><h3>Session duration</h3>
          <BarList color="#8b5cf6" items={(eng.durationBuckets || []).map((b) => ({ label: b.label, value: b.count }))} /></div>}
        {d.heat && <div className="sec-card" style={{ gridColumn: '1 / -1' }}><h3>When visitors arrive (UTC)</h3>
          <Heatmap days={d.heat.days} matrix={d.heat.matrix} /></div>}
      </div>
    </div>
  );
}
